/*
  Shoppable video cards.

  The markup ships a poster image only; the <video> lives in a <template> and is
  instantiated the first time the card scrolls into view. Nothing downloads for
  cards the visitor never reaches.
*/
class ShoppableVideo extends HTMLElement {
  connectedCallback() {
    if (this.observer) return;

    this.template = this.querySelector('template');
    if (!this.template) return;

    this.card = this.closest('.video-collection__card');
    this.autoplay = this.dataset.autoplay === 'true' && !ShoppableVideo.prefersReducedMotion();

    // Wired once here, not in load() — the button must respond on the first click
    // whether or not the clip has been instantiated yet.
    this.toggle = this.card && this.card.querySelector('.video-collection__sound');
    this.playButton = this.card && this.card.querySelector('.video-collection__play');
    this.controlClick = this.controlClick || this.onControlClick.bind(this);
    if (this.card) this.card.addEventListener('click', this.controlClick);

    if (this.autoplay) {
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0.5 });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.observer = null;
    if (this.card && this.controlClick) this.card.removeEventListener('click', this.controlClick);
  }

  static prefersReducedMotion() {
    return document.documentElement.dataset.ccMotion === 'reduce' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Audio is a single channel: several clips playing at once would just talk over
  // each other, so anything already audible is silenced first.
  static muteAll(except) {
    document.querySelectorAll('shoppable-video').forEach((clip) => {
      if (clip !== except && typeof clip.mute === 'function') clip.mute();
    });
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !ShoppableVideo.prefersReducedMotion()) {
        this.load();
        this.play();
      } else {
        this.pause();
      }
    });
  }

  onControlClick(event) {
    const button = event.target.closest('.video-collection__play, .video-collection__sound');
    if (!button || !this.card.contains(button)) return;
    this.playButton = this.card.querySelector('.video-collection__play');
    this.toggle = this.card.querySelector('.video-collection__sound');
    if (button.matches('.video-collection__play')) this.onPlayClick(event);
    else this.onToggleSound(event);
  }

  onPlayClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.video && !this.video.paused) {
      this.pause();
      return;
    }
    this.load();
    this.play();
  }

  load() {
    if (this.video) return;

    this.appendChild(this.template.content.firstElementChild.cloneNode(true));
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.video.playsInline = true;
    // Autoplaying clips stay silent and looping; a clip the visitor opened
    // deliberately gets sound. Native controls stay off in both modes — the
    // card's own play/pause and sound buttons are the only controls.
    this.video.muted = this.autoplay;
    this.video.loop = this.autoplay;
    this.video.controls = false;
    // The card class follows real playback: the end of a non-looping clip
    // pauses the video without going through pause().
    this.video.addEventListener('play', () => this.syncPlayingState(true));
    this.video.addEventListener('pause', () => this.syncPlayingState(false));
    this.video.addEventListener('ended', () => this.syncPlayingState(false));
    this.syncSoundState();
  }

  onToggleSound(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.video) return;
    if (this.video.muted) ShoppableVideo.muteAll(this);
    this.video.muted = !this.video.muted;
    this.syncSoundState();
  }

  syncSoundState() {
    if (!this.toggle || !this.video) return;
    this.toggle.setAttribute('aria-pressed', String(!this.video.muted));
    this.toggle.setAttribute('aria-label', this.video.muted ? this.toggle.dataset.unmuteLabel : this.toggle.dataset.muteLabel);
  }

  syncPlayingState(playing) {
    if (this.card) this.card.classList.toggle('is-playing', playing);
    if (this.playButton) {
      this.playButton.setAttribute('aria-pressed', String(playing));
      this.playButton.setAttribute('aria-label', playing ? this.playButton.dataset.pauseLabel : this.playButton.dataset.playLabel);
    }
  }

  /*
    Used when the quick view takes over the screen. Silencing rather than pausing
    is deliberate: the observer only reacts to intersection *changes*, and the card
    never leaves the viewport while the modal is open, so a paused clip would have
    nothing to restart it once the modal closes.
  */
  mute() {
    if (!this.video || this.video.muted) return;
    this.video.muted = true;
    this.syncSoundState();
  }

  play() {
    if (!this.video) return;
    document.querySelectorAll('shoppable-video').forEach(clip => {
      if (clip !== this && typeof clip.pause === 'function') clip.pause();
    });
    const played = this.video.play();
    if (played && typeof played.catch === 'function') {
      // Autoplay can still be refused (Low Power Mode); the poster just stays put.
      played.catch(() => this.syncPlayingState(false));
    }

  }

  pause() {
    if (!this.video) return;
    this.video.pause();

  }
}

if (!customElements.get('shoppable-video')) {
  customElements.define('shoppable-video', ShoppableVideo);
}

/*
  Cards stay real links so that crawlers, middle-click and "open in new tab" all
  keep working; a plain left click is upgraded to the theme's quick view, which
  keeps the visitor inside the row instead of sending them to the product page.
*/
function onShoppableCardClick(event) {
  const card = event.currentTarget;

  // Anything the browser would treat as "open elsewhere" is left alone.
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const wrapper = card.closest('.card-wrapper');
  const drawer = wrapper && wrapper.querySelector('quick-view-drawer');
  const summary = drawer && drawer.querySelector('summary');
  // No drawer means quick view never rendered — fall through to navigation.
  if (!summary) return;

  event.preventDefault();

  // Every clip, not just this card's — the audible one may be a different card.
  ShoppableVideo.muteAll();

  summary.click();
  document.dispatchEvent(
    new CustomEvent('quickview:open', { detail: { productUrl: card.getAttribute('href') } })
  );
}

function initShoppableCards(root) {
  (root || document).querySelectorAll('.video-collection__link[data-quick-view="true"]').forEach((card) => {
    if (card.dataset.quickViewBound) return;
    card.dataset.quickViewBound = 'true';
    card.addEventListener('click', onShoppableCardClick);
  });
}

initShoppableCards();
document.addEventListener('shopify:section:load', (event) => initShoppableCards(event.target));

// Native product-form retains basket rendering, errors and homepage measurement.
function initInlineVideoShopping(root = document) {
  root.querySelectorAll('form[data-video-buy]').forEach(form => {
    if (form.dataset.videoBuyBound) return;
    form.dataset.videoBuyBound = 'true';
    const select = form.querySelector('select[name="id"]');
    const quantity = form.querySelector('[name="quantity"]');
    const button = form.querySelector('[type="submit"]');
    const card = form.closest('.video-collection__card');
    function updateVariant() {
      const option = select.selectedOptions[0];
      if (!option) return;
      const available = option.dataset.available === 'true' && !option.disabled;
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
      button.querySelector('span').textContent = available ? button.dataset.addLabel : button.dataset.soldLabel;
      quantity.min = option.dataset.min || '1';
      quantity.dataset.min = quantity.min;
      quantity.step = option.dataset.step || '1';
      if (option.dataset.max) quantity.max = option.dataset.max;
      else quantity.removeAttribute('max');
      // Reset on option change so a previous variant's quantity rules cannot leak.
      quantity.value = quantity.min;
      quantity.dispatchEvent(new Event('change', {bubbles: true}));
      const price = card.querySelector('[data-video-price]');
      const compare = card.querySelector('[data-video-compare]');
      if (price) price.textContent = option.dataset.price;
      if (compare) {
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
      }
    }
    select.addEventListener('change', updateVariant);
    form.addEventListener('submit', event => {
      if (!select.selectedOptions[0] || select.selectedOptions[0].dataset.available !== 'true' || !form.checkValidity()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        form.reportValidity();
      }
    }, true);
    updateVariant();
  });
}
initInlineVideoShopping();
document.addEventListener('shopify:section:load', event => initInlineVideoShopping(event.target));
